import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const SITE_URL = 'https://staging.thelaundryco.online';

/**
 * The real content pages that exist in both an unprefixed (English) and
 * an /ar-prefixed (Arabic) variant -- router.tsx pairs each one. Anything
 * not in this list (account, checkout, /r/:code, privacy, ...) has no
 * language-mirrored URL at all, so no hreflang/canonical tags belong on
 * it; this component removes any stale ones and does nothing further.
 */
const MIRRORED_PATHS = ['/', '/about', '/services', '/why-us', '/contact'];

function arPathFor(path: string): string {
  return path === '/' ? '/ar' : `/ar${path}`;
}

/**
 * Every mirrored path except the true root is directory-backed in
 * production (nginx serves dist/<path>/index.html for it) -- requesting
 * it without a trailing slash gets a 301 to the slashed form first
 * (nginx's standard directory-redirect behaviour, confirmed live against
 * the real prerendered build). Pointing canonical/hreflang at the
 * slashed form directly means a crawler reaches the real content in one
 * request instead of two.
 */
function finalUrlPath(path: string): string {
  return path === '/' ? '/' : `${path}/`;
}

function setLink(rel: string, hreflang: string | null, href: string) {
  const selector = hreflang
    ? `link[rel="${rel}"][hreflang="${hreflang}"]`
    : `link[rel="${rel}"]:not([hreflang])`;
  let el = document.head.querySelector<HTMLLinkElement>(selector);
  if (!el) {
    el = document.createElement('link');
    el.rel = rel;
    if (hreflang) el.hreflang = hreflang;
    document.head.appendChild(el);
  }
  el.href = href;
}

function removeLinks(rel: string) {
  document.head.querySelectorAll(`link[rel="${rel}"]`).forEach((el) => el.remove());
}

/**
 * ar-BH / en-BH hreflang pair plus a self-referencing canonical, on every
 * marketing page that has a real language-mirrored URL. Mounted once in
 * MarketingLayout (like SchemaOrg) rather than per-page -- it derives
 * everything it needs from the current route itself.
 */
export function Hreflang() {
  const { pathname } = useLocation();

  useEffect(() => {
    const isAr = pathname === '/ar' || pathname.startsWith('/ar/');
    const canonicalPath = isAr ? (pathname === '/ar' ? '/' : pathname.slice('/ar'.length)) : pathname;

    if (!MIRRORED_PATHS.includes(canonicalPath)) {
      removeLinks('alternate');
      removeLinks('canonical');
      return;
    }

    const enHref = `${SITE_URL}${finalUrlPath(canonicalPath)}`;
    const arHref = `${SITE_URL}${finalUrlPath(arPathFor(canonicalPath))}`;

    setLink('alternate', 'en-BH', enHref);
    setLink('alternate', 'ar-BH', arHref);
    setLink('alternate', 'x-default', enHref);
    setLink('canonical', null, isAr ? arHref : enHref);
  }, [pathname]);

  return null;
}
